import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useGetMultichainCollectionMetadata, useGetMultichainCollectionTokenCount } from '@pkg/hooks';

import { type DaasMarketplaceCollection } from '@app/client';
import { callGetCollectionsMetadata, COLLECTION_METADATA_CACHE } from '@app/client/resources/competitions';
import { useFantasyCollectionMetadataStore } from '@app/store/zustand';

type UseGetCompetitionCollectionsMetadataProps = {
    searchQuery: string;
    disabled?: boolean;
    setMetadataStore?: boolean;
    ignoreSignal?: boolean;
};
export const useGetCompetitionCollectionsMetadata = ({
    searchQuery,
    disabled,
    setMetadataStore = true,
    ignoreSignal = false,
}: UseGetCompetitionCollectionsMetadataProps) => {
    const { setCollectionsMetadata } = useFantasyCollectionMetadataStore();
    const cache = [COLLECTION_METADATA_CACHE, searchQuery];

    return useQuery({
        queryKey: cache,
        queryFn: ({ signal }) =>
            callGetCollectionsMetadata({
                searchQuery,
                settings: { signal: ignoreSignal ? undefined : signal },
            }),
        placeholderData: keepPreviousData,
        enabled: Boolean(searchQuery?.length) && !disabled,
        select: (data) => {
            if (data?.ok && data.value?.length) {
                if (setMetadataStore) {
                    setCollectionsMetadata({ ethereum: data.value as unknown as DaasMarketplaceCollection[] });
                }
                return data.value;
            }
        },
    });
};

interface MultiChainCollectionsMetadataProps {
    setMetadataStore?: boolean;
    disabled?: boolean;
}
export const useAsyncGetCompetitionCollectionsMetadata = ({
    setMetadataStore = true,
    disabled = false,
}: MultiChainCollectionsMetadataProps) => {
    const { setCollectionsMetadata } = useFantasyCollectionMetadataStore();
    const { getCollectionMetadata } = useGetMultichainCollectionMetadata({ disabled, ignoreCancel: true });
    const { getMultichainTokenListingsCount } = useGetMultichainCollectionTokenCount({ disabled });

    return async (searchQuery: string[]) => {
        if (searchQuery?.length) {
            const slugQuery = searchQuery.slice(0, 20);
            // daas says no more than 20 slugs
            return getCollectionMetadata({ slugQuery })?.then(async (metadataRes) => {
                if (metadataRes?.ok && metadataRes.value) {
                    if (setMetadataStore) {
                        const tokenCountRes = await getMultichainTokenListingsCount({ slugQuery });
                        if (tokenCountRes?.ok && tokenCountRes?.value) {
                            setCollectionsMetadata(metadataRes.value, tokenCountRes.value);
                        } else {
                            setCollectionsMetadata(metadataRes.value);
                        }
                    }
                    return metadataRes;
                }
            });
        }
    };
};
